/* eslint-disable camelcase */
import axios from 'axios';
import { ElMessage, ElLoading } from 'element-plus';
import { getToken } from '@/utils/auth';
import errorCode from '@/utils/http/errorCode';
import { tansParams, blobValidate } from '@/utils/http/mei-mei';
import cache from '@/plugins/cache';
import { saveAs } from 'file-saver';
import useUserStore from '@/store/modules/user';
import { getCrmebAdminApiBase } from '@/config/crmebAdminApi';
import { roterPre } from '@/setting';

let downloadLoadingInstance: ReturnType<typeof ElLoading.service>;

// 是否显示重新登录
export const isRelogin = { show: false };

// 获取超时时间（从环境变量读取）
const timeout = Number(import.meta.env.VITE_APP_TIMEOUT) || 10000;

// 创建axios实例
const service = axios.create({
    // 对齐 EB request.js：baseURL = SettingMer.https（origin + /sys）
    baseURL: getCrmebAdminApiBase(),
    // 超时（从环境配置读取）
    timeout: timeout,
    headers: {
        'Content-Type': 'application/json;charset=utf-8',
    },
});

// 🔍 开发环境日志
if (import.meta.env.MODE === 'development') {
    // console.log('📡 Axios 配置:');
    // console.log('  baseURL:', getCrmebAdminApiBase());
    // console.log('  timeout:', timeout);
}

// request拦截器
service.interceptors.request.use(
    config => {
        // console.log('request拦截器111',config);
        // 是否需要设置 token
        const isToken = (config.headers || {}).isToken === false;
        // 是否需要防止数据重复提交
        const isRepeatSubmit = (config.headers || {}).repeatSubmit === false;
        if (getToken() && !isToken && config.headers) {
            config.headers['Authorization'] = 'Bearer ' + getToken(); // 让每个请求携带自定义token 请根据实际情况自行修改
        }
        // TITA 扩展：FormData 须由浏览器带 boundary；且 JSON.stringify(FormData) 恒为 "{}"，
        // 多图连传会被误判「重复提交」。对齐 tita_mer request 拦截器。
        const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
        if (isFormData && config.headers) {
            delete (config.headers as Record<string, unknown>)['Content-Type'];
        }
        // get请求映射params参数
        if (config.method === 'get' && config.params) {
            let url = config.url + '?' + tansParams(config.params);
            url = url.slice(0, -1);
            config.params = {};
            config.url = url;
        }
        if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put') && !isFormData) {
            const requestObj = {
                url: config.url,
                data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
                time: new Date().getTime(),
            };
            const sessionObj = cache.session.getJSON('sessionObj');
            if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
                cache.session.setJSON('sessionObj', requestObj);
            } else {
                const s_url = sessionObj.url; // 请求地址
                const s_data = sessionObj.data; // 请求数据
                const s_time = sessionObj.time; // 请求时间
                const interval = 1000; // 间隔时间(ms)，小于此时间视为重复提交
                if (
                    s_data === requestObj.data &&
                    requestObj.time - s_time < interval &&
                    s_url === requestObj.url
                ) {
                    const message = '数据正在处理，请勿重复提交';
                    console.warn(`[${s_url}]: ` + message);
                    return Promise.reject(new Error(message));
                } else {
                    cache.session.setJSON('sessionObj', requestObj);
                }
            }
        }
        // console.log('request拦截器222',config);
        return config;
    },
    error => {
        console.log(error);
        Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    res => {
        // EB 业务态：status（view/admin/src/api/request.js）
        const status = res.data.status ?? 200;
        const msg =
            errorCode[status] ||
            res.data.message ||
            errorCode['default'];
        // 二进制数据则直接返回
        if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
            return res.data;
        }
        // EB 登录态失效（view/admin/src/api/request.js）
        const CRMEB_LOGIN_EXPIRED_STATUS = [410000, 410001, 410002, 40000];
        if (CRMEB_LOGIN_EXPIRED_STATUS.includes(Number(status))) {
            if (!isRelogin.show) {
                isRelogin.show = true;
                    useUserStore()
                    .logout()
                    .then(() => {
                        location.href = `${roterPre}/login`;
                    })
                    .finally(() => {
                        isRelogin.show = false;
                    });
            }
            return Promise.reject(res.data);
        } else if (status === 500) {
            ElMessage({
                message: msg,
                type: 'error',
            });
            return Promise.reject(new Error(msg));
        } else if (status === 601) {
            ElMessage({
                message: msg,
                type: 'warning',
            });
            return Promise.reject(new Error(msg));
        } else if (status !== 200) {
            // 对齐 EB view/admin/src/api/request.js：业务失败只 reject { message }，由页面 $message.error
            // 勿在拦截器再 ElNotification，否则会和页面各弹一个（其中一个还没有文案）
            return Promise.reject(Object.assign(new Error(msg), { status, data: res.data }));
        } else {
            // 对齐 EB request.js：resolve 整包 { status, message, data }，页面 res.data.list
            return Promise.resolve(res.data);
        }
    },
    error => {
        // console.log('err' + error);
        let { message } = error;
        if (message === 'Network Error') {
            message = '后端接口连接异常';
        } else if (message.includes('timeout')) {
            message = '系统接口请求超时';
        } else if (message.includes('Request failed with status code')) {
            message = '系统接口' + message.substr(message.length - 3) + '异常';
        }
        ElMessage({
            message: message,
            type: 'error',
            duration: 5 * 1000,
        });
        return Promise.reject(error);
    }
);

// 通用下载方法
export function download(url: string, params: any, filename: string, config: any = {}) {
    downloadLoadingInstance = ElLoading.service({
        text: '正在下载数据，请稍候',
        background: 'rgba(0, 0, 0, 0.7)',
    });
    return service
        .post(url, params, {
            transformRequest: [
                params => {
                    return tansParams(params);
                },
            ],
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            responseType: 'blob',
            ...config,
        })
        .then(async (data: any) => {
            const isBlob = blobValidate(data);
            if (isBlob) {
                const blob = new Blob([data]);
                saveAs(blob, filename);
            } else {
                const resText = await data.text();
                const rspObj = JSON.parse(resText);
                const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default'];
                ElMessage.error(errMsg);
            }
            downloadLoadingInstance.close();
        })
        .catch(r => {
            console.error(r);
            ElMessage.error('下载文件出现错误，请联系管理员！');
            downloadLoadingInstance.close();
        });
}

export default service;
